/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import type { Inline } from '@contentful/rich-text-types';

import { Typography, Typography2, breakpoints } from '@coursera/cds-core';

import type { DdpFieldsBelowFoldFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import IconLabel from 'bundles/degree-description/components/shared/IconLabel';
import {
  filterRichTextContentByNodeType,
  isEmptyChild,
  renderRichText,
} from 'bundles/degree-description/components/shared/RichText';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import type { DegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';

type CareerSectionType = DdpFieldsBelowFoldFragment['careerSection'];
type CareerSectionLinksType = NonNullable<CareerSectionType>['links'];

type CareerIntroCardProps = {
  career: CareerSectionType;
};

const styles = {
  introCard: css`
    ${breakpoints.down('sm')} {
      margin: 0 var(--cds-spacing-200);
    }
  `,
  iconContainer: (theme: DegreeDescriptionPageTheme) => css`
    display: flex;
    align-items: center;

    .rc-IconList .description-container {
      ${theme.mobileFontSizeStyle?.body};
    }
  `,
  heading: css`
    margin-bottom: 20px;
  `,
  paddingBottom: css`
    padding-bottom: 10px;
  `,
  paragraphPadding: css`
    padding-bottom: var(--cds-spacing-300);
  `,
  listContainer: css`
    list-style: none;
    margin: unset;
    padding: unset;
  `,
};

const getRichTextRenderOptions = (links: CareerSectionLinksType): Options => {
  return {
    renderNode: {
      [BLOCKS.HEADING_3]: (_, children) => {
        if (isEmptyChild(children)) return null;
        const theme = useDegreeDescriptionPageTheme();
        return (
          <Typography variant="h1semibold" component="h2" css={[styles.heading, theme.mobileFontSizeStyle?.header]}>
            {children}
          </Typography>
        );
      },
      [BLOCKS.PARAGRAPH]: (node, children) => {
        if (isEmptyChild(children)) return null;
        const theme = useDegreeDescriptionPageTheme();
        if (node.content.length <= 1) {
          return (
            <Typography2
              component="p"
              variant="bodyPrimary"
              css={[styles.paragraphPadding, theme.mobileFontSizeStyle?.body]}
            >
              {children}
            </Typography2>
          );
        } else {
          return <ul css={[styles.listContainer, theme.mobileFontSizeStyle?.body]}>{children}</ul>;
        }
      },
      [INLINES.EMBEDDED_ENTRY]: (node) => {
        const theme = useDegreeDescriptionPageTheme();
        const inlineNode = node as Inline;
        const id = inlineNode?.data?.target?.sys?.id as string;
        const entry = links?.entries?.inline?.find((inline) => inline?.sys?.id === id);
        if (entry && entry.__typename === 'GeneralIconLabel') {
          return (
            <li css={styles.paddingBottom}>
              <IconLabel
                iconLabel={entry.iconLabel}
                iconVariant={entry.iconVariant}
                iconColor="interactive"
                iconContainerStyle={styles.iconContainer(theme)}
              />
            </li>
          );
        }
        return null;
      },
    },
  };
};

const CareerIntroCard = ({ career }: CareerIntroCardProps) => {
  if (!career) return null;
  const richTextRenderOptions = getRichTextRenderOptions(career.links);
  return (
    <div css={styles.introCard}>
      {renderRichText({
        content: filterRichTextContentByNodeType(career.json, [
          BLOCKS.HEADING_3,
          BLOCKS.PARAGRAPH,
          INLINES.EMBEDDED_ENTRY,
        ]),
        linkTrackingName: 'career_section_link',
        richTextRenderOptions,
      })}
    </div>
  );
};

export default CareerIntroCard;
